import { ReactNode } from 'react';
import { linkify } from './linkify';

// News are typed in a plain textarea. A light syntax gives them some structure:
// a blank line separates paragraphs, "## " starts a subheading, "- " a list item.
type Block = { kind: 'heading' | 'list' | 'paragraph'; lines: string[] };

const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  let current = null as Block | null;

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) {
      current = null;
    } else if (line.startsWith('## ')) {
      blocks.push({ kind: 'heading', lines: [line.slice(3)] });
      current = null;
    } else {
      const kind = line.startsWith('- ') ? 'list' : 'paragraph';
      const content = kind === 'list' ? line.slice(2) : line;
      if (current?.kind === kind) {
        current.lines.push(content);
      } else {
        current = { kind, lines: [content] };
        blocks.push(current);
      }
    }
  }
  return blocks;
};

export const renderArticle = (text: string): ReactNode[] =>
  toBlocks(text).map((block, index) => {
    if (block.kind === 'heading') {
      return <h2 key={index} className="heading text-2xl md:text-3xl mt-12 mb-4">{block.lines[0]}</h2>;
    }
    if (block.kind === 'list') {
      return (
        <ul key={index} className="list-disc pl-6 space-y-2 mb-6 marker:text-accent">
          {block.lines.map((line, i) => <li key={i}>{linkify(line)}</li>)}
        </ul>
      );
    }
    return <p key={index} className="mb-6 whitespace-pre-line">{linkify(block.lines.join('\n'))}</p>;
  });

// Text for previews (cards, admin list): the markers are dropped.
export const articleExcerpt = (text: string): string =>
  text
    .split('\n')
    .map(line => line.replace(/^\s*(## |- )/, '').trim())
    .filter(Boolean)
    .join(' ');
